import React from 'react'
import PropTypes from 'prop-types'
import { CaretDown, FunnelSimple } from 'phosphor-react'
import Pagination from './Pagination'
import SearchInput from './search-input/SearchInput'
import './TableWrapper.scss'

function TableWrapper({
  searchPlaceholder,
  searchValue,
  setSearchQuery,
  actionButtons,
  selectedItems,
  currentPage,
  pageSize,
  totalListCount,
  onPageChange,
  eventFilterHeader,
  totalLabel,
  footerVariant,
  setPerPage,
  children,
}) {
  return (
    <div
      className={`table-main ${
        eventFilterHeader ? 'table-main--event-filter' : 'table-main--default'
      }`}
    >
      <div className='table-head-content'>
        {eventFilterHeader ? (
          <div className='filter-row'>
            <div className='filter-row__left'>
              <button type='button' className='filter-row__dropdown-btn'>
                <FunnelSimple size={16} />
                <span>All</span>
                <CaretDown size={16} />
              </button>

              {selectedItems > 0 ? (
                <span className='selected-items-otr'>{`${selectedItems} items selected`}</span>
              ) : (
                <SearchInput
                  placeholder={searchPlaceholder || 'Search'}
                  value={searchValue}
                  handleChange={setSearchQuery}
                />
              )}
            </div>

            <div className='filter-row__right'>
              <div className='filter-row__actions'>
                {actionButtons?.length > 0 &&
                  actionButtons.map((item) => (
                    <button
                      key={item.label || item.ariaLabel}
                      type='button'
                      className={item.classes}
                      aria-label={item.ariaLabel}
                      onClick={item.handleClick}
                    >
                      {item?.icon && item.icon}
                      {!item?.icon && item.label}
                    </button>
                  ))}
              </div>
              <p className='filter-row__total'>
                <span>{totalLabel}</span>
                <strong>{totalListCount || 0}</strong>
              </p>
            </div>
          </div>
        ) : (
          <div className='search-otr'>
            {selectedItems > 0 ? (
              <span className='selected-items-otr'>{`${selectedItems} items selected`}</span>
            ) : (
              <SearchInput
                placeholder={searchPlaceholder || 'Search'}
                value={searchValue}
                handleChange={setSearchQuery}
              />
            )}
            {actionButtons?.length > 0 &&
              actionButtons.map((item) => (
                <button
                  key={item.label}
                  type='button'
                  className={item.classes}
                  onClick={item.handleClick}
                >
                  {item?.icon && item.icon}
                  {item.label}
                </button>
              ))}
          </div>
        )}
        {searchValue && (
          <p className='search-result'>
            {`Showing ${totalListCount} for `}
            <span className='search-value'>{searchValue}</span>
          </p>
        )}
      </div>
      <div className='table-otr'>
        {children}
        <div className={`footer-table${footerVariant === 'listing' ? ' footer-table--listing' : ''}`}>
          <div className='d-flex align-items-center'>
            <div className='text-otr mx-3'>
              <p className='entity-text heading-xs'>
                {footerVariant === 'listing'
                  ? `Page ${currentPage} of ${Math.max(
                      1,
                      Math.ceil((totalListCount || 0) / pageSize)
                    )}`
                  : `Showing ${(currentPage - 1) * pageSize + 1} to ${
                      (currentPage - 1) * pageSize > totalListCount
                        ? totalListCount
                        : currentPage * pageSize
                    } of ${totalListCount} Entries`}
              </p>
            </div>
          </div>
          <Pagination
            itemsCount={totalListCount}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={onPageChange}
            variant={footerVariant}
          />
          {footerVariant === 'listing' && (
            <label className='page-size'>
              <select
                aria-label='Rows per page'
                value={pageSize}
                onChange={(event) =>
                  setPerPage({ value: Number(event.target.value) })
                }
              >
                {[10, 20, 50].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>
    </div>
  )
}

TableWrapper.propTypes = {
  searchPlaceholder: PropTypes.string,
  searchValue: PropTypes.string,
  setSearchQuery: PropTypes.func,
  actionButtons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      classes: PropTypes.string.isRequired,
      handleClick: PropTypes.func.isRequired,
      icon: PropTypes.element,
    })
  ),
  selectedItems: PropTypes.number,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  totalListCount: PropTypes.number,
  onPageChange: PropTypes.func,
  eventFilterHeader: PropTypes.bool,
  totalLabel: PropTypes.string,
  footerVariant: PropTypes.string,
  setPerPage: PropTypes.func,
  children: PropTypes.node,
}

TableWrapper.defaultProps = {
  searchPlaceholder: '',
  searchValue: '',
  setSearchQuery: () => {}, // Provide a default function if needed
  actionButtons: [],
  selectedItems: 0,
  currentPage: 1,
  pageSize: 10, // Adjust the default value based on your requirements
  totalListCount: 0,
  onPageChange: () => {}, // Provide a default function if needed
  eventFilterHeader: false,
  totalLabel: 'Total',
  footerVariant: '',
  setPerPage: () => {},
  children: null,
}

export default TableWrapper
